import React, {useCallback, useMemo, useState} from 'react';
import {Alert, Button, Checkbox, Input, message, Spin, Space, Typography} from 'antd';
import {ArrowLeftOutlined, DownOutlined, LeftOutlined, RightOutlined, UpOutlined} from '@ant-design/icons';
import {useLazyQuery, useMutation, useQuery} from '@apollo/client';
import {useLocation, useNavigate, useParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {
  GET_QC_POD_EXEMPT_ACTIVE_FENCES,
  GET_QC_POD_EXEMPT_NEARBY,
  GET_WMS_POD_EXEMPT_ACTIVE_FENCES,
  GET_QC_POD_EXEMPT_REPORTS,
  GET_QC_POD_EXEMPT_REPORT_DETAIL,
  GET_WMS_POD_EXEMPT_NEARBY,
  GET_WMS_POD_EXEMPT_REPORTS,
  GET_WMS_POD_EXEMPT_REPORT_DETAIL,
  QcPodExemptReportDetailResponse,
  QcPodExemptReportsResponse,
  WmsPodExemptReportDetailResponse,
  WmsPodExemptReportsResponse,
  GET_WMS_POD_EXEMPT_POD_SAMPLES,
  GET_QC_POD_EXEMPT_POD_SAMPLES,
  WmsPodExemptPodSamplesData,
  QcPodExemptPodSamplesData,
} from '../../graphql/podExempt/queries';
import {
  FINAL_REVIEW_POD_EXEMPT_REPORT,
  FIRST_REVIEW_POD_EXEMPT_REPORT,
} from '../../graphql/podExempt/mutations';
import {
  PodExemptPodSampleType,
  PodExemptReportDetailType,
  PodExemptStatus,
} from '../../graphql/podExempt/types';
import {translateGraphQLError} from '../../utils/graphqlUtils';
import {usePodExemptDict} from '../../hooks/usePodExemptDict';
import {useWarehouseContext} from '../../contexts/WarehouseContext';
import PodExemptStatusTag from './PodExemptStatusTag';
import PodExemptFenceDrawMap from './PodExemptFenceDrawMap';
import PodExemptFlowTimeline from './PodExemptFlowTimeline';
import PodExemptNearbyPanel from './PodExemptNearbyPanel';
import {parseFencePoints} from './PodExemptDetailContent';
import PodExemptMaterialGallery from './PodExemptMaterialGallery';
import PodExemptOpinionCards from './PodExemptOpinionCards';
import PodExemptPodGallery from './PodExemptPodGallery';

export type PodExemptReviewRole = 'WHS' | 'QC';

interface Props {
  role: PodExemptReviewRole;
}

/** 审核要点卡：颜色语义对齐原型（good 绿 / warn 黄 / plain 灰）；数值未接入显示 - */
/** 司机样本充足阈值：≥4 名不同司机可交叉验证（原型口径）。 */
const DRIVER_SAMPLE_SUFFICIENT = 4;

/**
 * 指标取值：null 一律显示「-」而不是 0。
 * 「拿不到数据」与「数值为零」语义完全不同 —— 客诉显示 0 会让审核员误判该地址无客诉。
 */
const metricValue = (value: number | null | undefined): string => (value == null ? '-' : String(value));


const SignalCard: React.FC<{label: string; value: React.ReactNode; hint?: string; tone?: 'good' | 'warn' | 'plain'}> = ({
  label,
  value,
  hint,
  tone = 'plain',
}) => {
  const toneClass =
    tone === 'good'
      ? 'border-green-200 bg-green-50'
      : tone === 'warn'
        ? 'border-yellow-200 bg-yellow-50'
        : 'border-gray-200 bg-gray-50';
  return (
    <div className={`rounded-md border px-3.5 py-3 ${toneClass}`}>
      <div className="text-xs text-gray-500">{label}</div>
      <div className="text-lg font-semibold my-0.5">{value}</div>
      {hint && <div className="text-xs text-gray-400">{hint}</div>}
    </div>
  );
};

/** 原型卡片：白底圆角+浅阴影，标题下无分割线（antd Card 的 header 底线与原型不符，弃用） */
const Section: React.FC<{title: React.ReactNode; extra?: React.ReactNode; children: React.ReactNode}> = ({
  title,
  extra,
  children,
}) => (
  <div className="bg-white rounded-lg border border-gray-100 shadow-sm px-5 py-4">
    <div className="flex justify-between items-center mb-3.5">
      <span className="text-sm font-semibold">{title}</span>
      {extra}
    </div>
    {children}
  </div>
);

/** 原型「上报信息」字段行：灰标签固定宽 + 淡底线 */
const InfoField: React.FC<{label: React.ReactNode; value?: React.ReactNode}> = ({label, value}) => (
  <div className="flex gap-3 items-baseline py-2 border-b border-gray-100 min-w-0">
    <span className="text-gray-400 text-sm flex-none w-24">{label}</span>
    <span className="break-words min-w-0 flex-1">{value ?? '-'}</span>
  </div>
);

/**
 * 审核工作台页（对齐原型「门牌免拍审核-可交互」）：初审(仓管)/终审(QC)共用的双角色形态。
 * 队列点「审核」进入本页；顶栏支持在待审集合内上一条/下一条循环。
 */
const PodExemptReviewWorkbench: React.FC<Props> = ({role}) => {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const {reportId} = useParams<{reportId: string}>();

  const isCentral = role === 'QC';
  const pendingStatus: PodExemptStatus = isCentral ? 'PENDING_FINAL' : 'PENDING_FIRST';
  const listPath = isCentral ? '/qualitycontrol/pod-exempt' : '/wms/pod-exempt';
  /**
   * 列表页的筛选与分页挂在本页 URL 的 query 上（由列表页跳转时带过来）。
   * 「返回列表」原样拼回去，审核完不会把选好的「待初审」和页码清掉。
   * 上一条/下一条、周边参考跳转也要带上，否则翻一条 query 就丢了、再返回又是空筛选。
   */
  const listSearch = useLocation().search;
  const listUrl = `${listPath}${listSearch}`;
  // 客诉页两侧是同一组件按 mode 分叉（仓管 wms / 质控 HQ），故此处只需选路径
  // 仓管侧接口的 warehouseId 必填（资源码 + 仓权限双重鉴权）；QC 全美可见，无此维度
  const {currentWarehouse} = useWarehouseContext();
  const warehouseId = currentWarehouse?.id ?? null;
  const wmsReady = !isCentral && warehouseId != null;

  const [decision, setDecision] = useState<'approve' | 'reject' | null>(null);
  const [rejectCodes, setRejectCodes] = useState<string[]>([]);
  const [opinion, setOpinion] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // 原型：围栏默认折叠，点「查看围栏」才展开地图
  const [fenceOpen, setFenceOpen] = useState(false);
  // 切单必须清空上一单的审核结论：路由 /review/:reportId 只换 param 不重挂载，
  // decision/rejectCodes/opinion 是普通 state，会带到下一单造成串单误审（周边参考跳转、上一/下一条都走这条）
  React.useEffect(() => {
    setDecision(null);
    setRejectCodes([]);
    setOpinion('');
    setFenceOpen(false);
  }, [reportId]);
  // 审核端活跃围栏（原型：地图叠加同邮编「已生效/待审」围栏）；展开地图时才拉，围栏随审核实时变化不走缓存
  const [fetchActiveFences, {data: activeFencesData}] = useLazyQuery<
    Record<string, {status: string; pointsJson: string | null}[]>
  >(isCentral ? GET_QC_POD_EXEMPT_ACTIVE_FENCES : GET_WMS_POD_EXEMPT_ACTIVE_FENCES, {
    fetchPolicy: 'network-only',
  });
  React.useEffect(() => {
    if (!fenceOpen || reportId == null) {
      return;
    }
    if (isCentral) {
      fetchActiveFences({variables: {reportId}});
    } else if (warehouseId != null) {
      fetchActiveFences({variables: {reportId, warehouseId}});
    }
  }, [fenceOpen, reportId, isCentral, warehouseId, fetchActiveFences]);

  const {data: wmsDetail, loading: wmsLoading} = useQuery<WmsPodExemptReportDetailResponse>(
    GET_WMS_POD_EXEMPT_REPORT_DETAIL,
    {variables: {reportId, warehouseId: warehouseId!}, skip: !wmsReady || reportId == null, fetchPolicy: 'network-only'},
  );
  const {data: qcDetail, loading: qcLoading} = useQuery<QcPodExemptReportDetailResponse>(
    GET_QC_POD_EXEMPT_REPORT_DETAIL,
    {variables: {reportId}, skip: !isCentral || reportId == null, fetchPolicy: 'network-only'},
  );
  /**
   * 待审队列：**恒按全局取**（该角色数据域内的全部待审单），不再用列表页 navigate 带来的
   * state.pendingIds。列表页那份是「当前那一页 + 当前筛选」的子集，会导致：
   * 从第 2 页点进来只能在那几条里翻、当页恰好没有待审时显示「第 - / 0 条」、刷新即丢。
   * 上限 200（待审量级为数据域内百级，足够覆盖一轮连续审）。
   */
  const {data: wmsPending} = useQuery<WmsPodExemptReportsResponse>(GET_WMS_POD_EXEMPT_REPORTS, {
    variables: {
      filters: {statuses: [pendingStatus], warehouseId: warehouseId!},
      pageRequest: {page: 1, size: 200},
    },
    skip: !wmsReady,
  });
  const {data: qcPending} = useQuery<QcPodExemptReportsResponse>(GET_QC_POD_EXEMPT_REPORTS, {
    variables: {filters: {statuses: [pendingStatus]}, pageRequest: {page: 1, size: 200}},
    skip: !isCentral,
  });

  const [firstReview] = useMutation(FIRST_REVIEW_POD_EXEMPT_REPORT);
  const [finalReview] = useMutation(FINAL_REVIEW_POD_EXEMPT_REPORT);

  const {options: reasonDict, items: reasonDictAll} = usePodExemptDict('REASON');
  const {options: addressTypeDict, items: addressTypeDictAll} = usePodExemptDict('ADDRESS_TYPE');
  const {options: rejectDict, items: rejectDictAll} = usePodExemptDict('REJECT');
  const dictNameOf = useMemo(() => {
    const map = new Map<string, string>();
    [...reasonDictAll, ...addressTypeDictAll, ...rejectDictAll].forEach((item) =>
      map.set(item.code, item.name),
    );
    return (code: string) => map.get(code) ?? code;
  }, [reasonDictAll, addressTypeDictAll, rejectDictAll]);

  const detail: PodExemptReportDetailType | null =
    (isCentral ? qcDetail?.qcPodExemptReportDetail : wmsDetail?.wmsPodExemptReportDetail) ?? null;
  const loading = isCentral ? qcLoading : wmsLoading;
  const metrics = detail?.metrics ?? null;
  /**
   * 指标不可用：无 metrics 行（没有任何数据源：ODS 未回流且自算未开）或 PENDING（自算已投递未完成）。
   * 两种情况三张卡的数值位一律「-」；状态只在区块右上角用一段静态文字说一次，不转圈——
   * 转圈会让审核员以为页面卡了，而生产上 metricsEnabled=false 时 null 是常态、永远不会"转完"。
   */
  const metricsUnavailable = !metrics || metrics.calcStatus === 'PENDING';
  /** 区块右上角状态小字：null=「暂无统计」（别等了）/ PENDING=「统计中」（稍后再看）/ 其余不显示 */
  const metricsStatusLabel = !metrics
    ? t('podExempt.review.noMetrics')
    : metrics.calcStatus === 'PENDING'
      ? t('podExempt.review.calculating')
      : null;
  /** 非正常态的横幅提示；正常与统计中均为 null（统计中已由卡片内文案表达） */
  const metricsNotice = useMemo(() => {
    switch (metrics?.calcStatus) {
      case 'UNSTANDARDIZED':
        return t('podExempt.review.metricsUnstandardized');
      case 'FAILED':
        return t('podExempt.review.metricsFailed');
      case 'PARTIAL':
        return t('podExempt.review.metricsPartial');
      default:
        return null;
    }
  }, [metrics?.calcStatus, t]);

  // POD 画廊图片走懒加载：详情主查询不含 URL（presign 仅 10 分钟有效，提前签发会过期），
  // 由画廊组件在滚入视口时触发本查询，未展开的审核单不产生图片流量
  const [fetchWmsPodSamples] = useLazyQuery<WmsPodExemptPodSamplesData>(GET_WMS_POD_EXEMPT_POD_SAMPLES, {
    fetchPolicy: 'network-only',
  });
  const [fetchQcPodSamples] = useLazyQuery<QcPodExemptPodSamplesData>(GET_QC_POD_EXEMPT_POD_SAMPLES, {
    fetchPolicy: 'network-only',
  });
  const loadPodSamples = useCallback(async (): Promise<PodExemptPodSampleType[]> => {
    if (reportId == null) {
      return [];
    }
    if (isCentral) {
      const {data} = await fetchQcPodSamples({variables: {reportId}});
      return data?.qcPodExemptPodSampleImages ?? [];
    }
    // 图片接口只按 reportId 取：仓数据域由后端 detailForWarehouse 内部校验兜住
    // （能看详情才能看图），故无需传 warehouseId
    const {data} = await fetchWmsPodSamples({variables: {reportId}});
    return data?.wmsPodExemptPodSampleImages ?? [];
  }, [reportId, isCentral, fetchQcPodSamples, fetchWmsPodSamples]);

  const pendingIds = useMemo(() => {
    const content = isCentral
      ? qcPending?.qcPodExemptReports.content
      : wmsPending?.wmsPodExemptReports.content;
    return (content ?? []).map((row) => row.id);
  }, [isCentral, qcPending, wmsPending]);

  const currentIndex = reportId != null ? pendingIds.indexOf(reportId) : -1;
  const gotoOffset = (offset: number) => {
    if (pendingIds.length === 0) {
      return;
    }
    const base = currentIndex >= 0 ? currentIndex : 0;
    const next = pendingIds[(base + offset + pendingIds.length) % pendingIds.length];
    setDecision(null);
    setRejectCodes([]);
    setOpinion('');
    setFenceOpen(false);
    navigate(`${listPath}/review/${next}${listSearch}`);
  };

  const isPending = detail?.report.status === pendingStatus;
  const ready = decision === 'approve' || (decision === 'reject' && rejectCodes.length > 0);

  // 原型：流转记录末尾追加"等待 X 审"灰节点（待初审从提交时间起算，待终审从初审通过起算）
  const pendingNode = useMemo(() => {
    if (!detail) {
      return undefined;
    }
    const waited = (since: string | null | undefined) => {
      if (!since) {
        return undefined;
      }
      const hours = Math.max(0, Math.floor((Date.now() - new Date(since).getTime()) / 3600000));
      return t('podExempt.review.waitedSla', {hours}) ?? undefined;
    };
    if (detail.report.status === 'PENDING_FIRST') {
      return {label: t('podExempt.review.waitingFirst'), hint: waited(detail.report.createdAt)};
    }
    if (detail.report.status === 'PENDING_FINAL') {
      const firstPassAt = [...detail.flows].reverse().find((flow) => flow.action === 'FIRST_PASS')?.createdAt;
      return {label: t('podExempt.review.waitingFinal'), hint: waited(firstPassAt ?? detail.report.createdAt)};
    }
    return undefined;
  }, [detail, t]);

  // 位置核验坐标：单地址取地址坐标，围栏取中心点
  const coords = useMemo(() => {
    if (detail?.address?.latitude != null && detail.address.longitude != null) {
      return {lat: detail.address.latitude, lng: detail.address.longitude};
    }
    if (detail?.fence?.centerLatitude != null && detail.fence.centerLongitude != null) {
      return {lat: detail.fence.centerLatitude, lng: detail.fence.centerLongitude};
    }
    return null;
  }, [detail]);

  const handleSubmit = async () => {
    if (!ready) {
      message.warning(
        decision === 'reject'
          ? t('podExempt.review.rejectCodesRequired')
          : t('podExempt.review.conclusionRequired'),
      );
      return;
    }
    if (reportId == null) {
      return;
    }
    // 仓管初审的 warehouseId 后端非空。此前仅靠「详情 skip → 按钮不渲染」间接兜住，
    // 一旦那条链变动就会提交 null 并回落成通用「审核失败」，用户无从自诊
    if (!isCentral && warehouseId == null) {
      message.warning(t('podExempt.create.warehouseRequired'));
      return;
    }
    setSubmitting(true);
    try {
      const pass = decision === 'approve';
      const input = {
        reportId,
        pass,
        rejectCodes: pass ? null : rejectCodes,
        opinion: opinion.trim() || null,
      };
      if (isCentral) {
        await finalReview({variables: {input}});
      } else {
        // 仓管初审须带 warehouseId 供仓权限校验（上方已判空，此处必非 null）
        await firstReview({variables: {input: {...input, warehouseId}}});
      }
      message.success(t('podExempt.review.success'));
      // 原型口径：提交后回队列
      navigate(listUrl);
    } catch (e) {
      message.error(translateGraphQLError(e, 'podExempt.review.failed'));
    } finally {
      setSubmitting(false);
    }
  };

  const report = detail?.report;
  const fenceCount = report?.addressCount ?? detail?.fence?.addressCount ?? null;
  // 原型标题：围栏单带「（围栏 · N 地址）」后缀，单地址直接展示地址
  const titleText = report
    ? `${report.reportNo} · ${detail?.address?.rawAddress ?? detail?.fence?.name ?? ''}${
        report.scopeType === 'FENCE'
          ? t('podExempt.review.fenceTitleSuffix', {num: fenceCount ?? '-'})
          : ''
      }`
    : '';
  const zipWarehouseMeta = report
    ? [
        report.zipcode,
        report.warehouseName ?? report.warehouseCode,
        report.scopeType === 'FENCE'
          ? fenceCount != null
            ? t('podExempt.field.addressCountUnit', {count: fenceCount})
            : t('podExempt.field.addressCounting')
          : t('podExempt.scope.single'),
      ]
        .filter(Boolean)
        .join(' · ')
    : '-';

  return (
    <div className="p-4">
      {/* 原型页头：标题 + 流程副标题（原型右侧的「当前身份」切换是演示开关，真实系统按登录角色分端） */}
      <div className="flex items-center gap-3 mb-3">
        <span className="text-xl font-semibold">
          {t(isCentral ? 'podExempt.menu.qc' : 'podExempt.menu.wms')}
        </span>
        <span className="text-gray-400 text-sm">
          {t(isCentral ? 'podExempt.review.pageSubtitleFinal' : 'podExempt.review.pageSubtitleFirst')}
        </span>
      </div>

      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3 min-w-0">
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(listUrl)}>
            {t('podExempt.review.backToList')}
          </Button>
          <span className="text-lg font-semibold truncate">{titleText}</span>
          {report && <PodExemptStatusTag status={report.status} />}
        </div>
        {/* 只在「当前这单确实在待审队列里」时显示翻页器：它是连续审待审单的工具。
            看已审完的单、或从周边参考跳进来的历史单时，原先会显示「第 - / N 条」，
            队列为空时更是「第 - / 0 条」加两个灰按钮，像坏了一样 */}
        {currentIndex >= 0 && (
          <Space>
            <span className="text-gray-500 text-sm">
              {t(isCentral ? 'podExempt.review.pagerFinal' : 'podExempt.review.pagerFirst', {
                index: currentIndex + 1,
                total: pendingIds.length,
              })}
            </span>
            <Button icon={<LeftOutlined />} disabled={pendingIds.length <= 1} onClick={() => gotoOffset(-1)} />
            <Button icon={<RightOutlined />} disabled={pendingIds.length <= 1} onClick={() => gotoOffset(1)} />
          </Space>
        )}
      </div>

      {loading || !detail || !report ? (
        <Spin />
      ) : (
        // 原型栏比：左 flex 1 1 620px / 右 flex 1 1 340px，两栏随屏宽等比放大
        <div className="flex flex-wrap gap-4 items-start">
          <div className="flex-[1_1_620px] min-w-0 flex flex-col gap-4">
            <Section title={t('podExempt.review.signalsTitle')}>
              {/* 机审通过率已按产品拍板移除（2026-09-02）：豁免生效后司机不再拍门牌，机审样本会枯竭，该指标在审核场景失真 */}
              {/* 三种非正常态各有明确文案，避免审核员把「-」误读成「零」：
                  UNSTANDARDIZED=地址标准化失败（允许上报，靠材料判断）/ FAILED=统计失败（非永远统计中）/ PARTIAL=部分统计 */}
              {metricsNotice && (
                <Alert
                  className="mb-3"
                  type={metrics?.calcStatus === 'FAILED' ? 'error' : 'warning'}
                  showIcon
                  message={metricsNotice}
                />
              )}
              {metricsStatusLabel && (
                <div className="flex justify-end -mt-1 mb-1 text-xs text-gray-400">{metricsStatusLabel}</div>
              )}
              <div className="grid grid-cols-3 gap-3 mb-4">
                <SignalCard
                  label={t('podExempt.review.driverSample')}
                  value={
                    metricsUnavailable
                      ? '-'
                      : t('podExempt.review.driverSampleValue', {
                          drivers: metrics?.distinctDriverCnt ?? 0,
                          photos: metrics?.podPhotoCnt ?? 0,
                        })
                  }
                  hint={
                    metricsUnavailable
                      ? undefined
                      : ((metrics?.distinctDriverCnt ?? 0) >= DRIVER_SAMPLE_SUFFICIENT
                          ? t('podExempt.review.driverPodHint')
                          : t('podExempt.review.driverPodHintThin')) ?? undefined
                  }
                  // 底色按原型固定，不随数据变；充足/偏少的语义由上面的 hint 文案承担
                  tone="good"
                />
                <SignalCard
                  label={t('podExempt.review.orders7d')}
                  value={metricValue(metrics?.orderCnt7d)}
                  hint={
                    t('podExempt.review.orders7dHint', {num: metricValue(metrics?.orderCnt30d)}) ?? undefined
                  }
                />
                <SignalCard
                  label={t('podExempt.review.complaints')}
                  value={metricValue(metrics?.complaintCnt90d)}
                  hint={t('podExempt.review.complaintsHint') ?? undefined}
                  // 同上：底色固定，不因客诉为 0 就退成灰色
                  tone="warn"
                />
              </div>
              <div className="flex flex-col gap-1.5 text-sm">
                <div>
                  <span className="text-gray-500 mr-2">{t('podExempt.field.scopeType')}:</span>
                  {report.scopeType === 'FENCE' ? t('podExempt.scope.fence') : t('podExempt.scope.single')}
                </div>
                <div>
                  <span className="text-gray-500 mr-2">{t('podExempt.field.addressType')}:</span>
                  {report.addressType ? dictNameOf(report.addressType) : '-'}
                </div>
                <div>
                  <span className="text-gray-500 mr-2">{t('podExempt.review.reasonLabel')}:</span>
                  {report.reasonCodes.map(dictNameOf).join(', ') || '-'}
                </div>
                <div>
                  <span className="text-gray-500 mr-2">{t('podExempt.review.dspNote')}:</span>
                  {report.reasonRemark ?? '-'}
                </div>
                {/* 驳回原因 / 驳回意见不在此处渲染：统一由下方 PodExemptOpinionCards 出卡，
                    否则同一条驳回会在「审核要点」与意见卡两处重复出现 */}
              </div>
            </Section>

            <Section title={t('podExempt.review.reportInfo')}>
              <div className="grid grid-cols-2 gap-x-8">
                <InfoField label={t('podExempt.filter.reportId')} value={report.reportNo} />
                <InfoField
                  label={t('podExempt.field.addressOrFence')}
                  value={detail.address?.rawAddress ?? detail.fence?.name ?? '-'}
                />
                <InfoField label={t('podExempt.review.zipWarehouse')} value={zipWarehouseMeta} />
                <InfoField
                  label={t('podExempt.review.dspReporter')}
                  value={[report.dspName, report.creatorName].filter(Boolean).join(' · ') || '-'}
                />
                <InfoField
                  label={t('podExempt.review.submitTime')}
                  value={report.createdAt ? new Date(report.createdAt).toLocaleString() : '-'}
                />
                {report.trackingNumber && (
                  <InfoField label={t('podExempt.field.waybillNo')} value={report.trackingNumber} />
                )}
              </div>
            </Section>

            {/* 审核意见卡：仓管初审 + 中台终审各一张，初审页同样展示中台驳回意见
                （打回重审的单，初审员需要知道被驳原因） */}
            <PodExemptOpinionCards flows={detail.flows} dictNameOf={dictNameOf} />

            <Section title={t('podExempt.review.podSamples')}>
              <PodExemptPodGallery metrics={metrics} loadSamples={loadPodSamples} />
            </Section>

            <Section title={t('podExempt.review.materialsSection')}>
              <PodExemptMaterialGallery materials={detail.materials} reasonCodes={detail.report.reasonCodes} />
              {detail.fence && (
                <div className="mt-3">
                  {/* 原型：上报围栏折叠条，点「查看围栏」才展开地图 */}
                  <div className="border border-gray-100 rounded-md px-3.5 py-2.5 flex items-center justify-between gap-3">
                    <span className="min-w-0 truncate">
                      <span className="font-medium mr-3">{t('podExempt.review.fenceSection')}</span>
                      <span className="text-gray-400 text-sm">
                        {zipWarehouseMeta} · {t('podExempt.review.fenceBarNote')}
                      </span>
                    </span>
                    <Typography.Link className="flex-none" onClick={() => setFenceOpen((open) => !open)}>
                      {t(fenceOpen ? 'podExempt.review.collapseFence' : 'podExempt.review.viewFence')}{' '}
                      {fenceOpen ? <UpOutlined /> : <DownOutlined />}
                    </Typography.Link>
                  </div>
                  {fenceOpen && (
                    <div className="mt-3">
                      {coords && (
                        <div className="text-gray-400 text-xs mb-2">
                          {coords.lat}, {coords.lng}
                        </div>
                      )}
                      {/* 全屏查看走地图自带控件 */}
                      <PodExemptFenceDrawMap
                        zipcode={detail.fence.zipcode}
                        points={parseFencePoints(detail.fence.pointsJson)}
                        onPointsChange={() => undefined}
                        readonly
                        activeFences={
                          activeFencesData?.[
                            isCentral ? 'qcPodExemptActiveFences' : 'wmsPodExemptActiveFences'
                          ] ?? []
                        }
                      />
                    </div>
                  )}
                </div>
              )}
            </Section>

            <div className="grid grid-cols-2 gap-4">
              <Section title={t('podExempt.review.moreData')}>
                {/* 妥投异常率已按产品拍板移除（2026-09-03）；原型上的环比趋势不做——需再扫一遍上一周期，成本翻倍且非决策必需 */}
                <div className="flex flex-col gap-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">{t('podExempt.review.orders30d')}</span>
                    <span>{metricValue(metrics?.orderCnt30d)}</span>
                  </div>
                  {metrics?.calculatedAt && (
                    <div className="text-xs text-gray-400">
                      {t('podExempt.review.dataAsOf', {time: new Date(metrics.calculatedAt).toLocaleString()})}
                    </div>
                  )}
                </div>
              </Section>
              <Section
                title={t('podExempt.review.locationCheck')}
                extra={
                  coords ? (
                    <span className="text-gray-400 text-xs">
                      {coords.lat}, {coords.lng}
                    </span>
                  ) : undefined
                }
              >
                {coords ? (
                  <>
                    <div className="grid grid-cols-2 gap-2 mb-2">
                      <Button
                        type="primary"
                        href={`https://www.google.com/maps/@${coords.lat},${coords.lng},18z/data=!3m1!1e3`}
                        target="_blank"
                      >
                        {t('podExempt.review.satellite')} ↗
                      </Button>
                      <Button
                        href={`https://www.google.com/maps?layer=c&cbll=${coords.lat},${coords.lng}`}
                        target="_blank"
                      >
                        {t('podExempt.review.streetView')} ↗
                      </Button>
                    </div>
                    <Typography.Text type="secondary" style={{fontSize: 12}}>
                      {t(
                        report.scopeType === 'FENCE'
                          ? 'podExempt.review.satelliteHintFence'
                          : 'podExempt.review.satelliteHintSingle',
                      )}
                    </Typography.Text>
                  </>
                ) : (
                  <Typography.Text type="secondary">{t('podExempt.field.coordinates')}: -</Typography.Text>
                )}
              </Section>
            </div>

            <Section
              title={
                <span>
                  {t('podExempt.review.nearbyRefTitle')}{' '}
                  <span className="text-gray-400 text-xs font-normal">
                    {t('podExempt.review.nearbySubtitle')}
                  </span>
                </span>
              }
            >
              {/* 仓管侧须等仓上下文就绪：wms 接口的 warehouseId 契约非空（$warehouseId: Long!），
                  未就绪就发会被变量校验拒掉。且面板内 `!called` 是不可逆闩锁 ——
                  失败后 warehouseId 到位也不会自动重查，只会留下「附近无豁免目标」的假空态误导审核 */}
              {reportId != null && (isCentral || wmsReady) && (
                <PodExemptNearbyPanel
                  reportId={reportId}
                  autoLoad
                  hideTitle
                  dictNameOf={dictNameOf}
                  query={isCentral ? GET_QC_POD_EXEMPT_NEARBY : GET_WMS_POD_EXEMPT_NEARBY}
                  responseKey={isCentral ? 'qcPodExemptNearbyEffective' : 'wmsPodExemptNearbyEffective'}
                  extraVariables={isCentral ? undefined : {warehouseId}}
                  onOpenReport={(nearbyReportId) =>
                    navigate(`${listPath}/review/${nearbyReportId}${listSearch}`)
                  }
                />
              )}
            </Section>
          </div>

          <div className="flex-[1_1_340px] min-w-[340px] flex flex-col gap-4">
            <Section
              title={t(isCentral ? 'podExempt.review.decisionFinal' : 'podExempt.review.decisionFirst')}
            >
              {!isPending ? (
                <Typography.Text type="secondary">{t('podExempt.review.readonlyHint')}</Typography.Text>
              ) : (
                <div className="flex flex-col gap-3">
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      type={decision === 'approve' ? 'primary' : 'default'}
                      onClick={() => setDecision('approve')}
                    >
                      {t(isCentral ? 'podExempt.review.approveFinal' : 'podExempt.review.approveFirst')}
                    </Button>
                    <Button
                      danger={decision === 'reject'}
                      type={decision === 'reject' ? 'primary' : 'default'}
                      onClick={() => setDecision('reject')}
                    >
                      {t('podExempt.review.reject')}
                    </Button>
                  </div>
                  {decision === 'reject' && (
                    <div>
                      <div className="text-xs text-gray-500 mb-1">{t('podExempt.review.rejectCodes')}</div>
                      <Checkbox.Group
                        className="flex flex-col gap-1.5"
                        value={rejectCodes}
                        // 不限勾选数量（2026-09-11 用户拍板）；列宽保护由后端按拼接长度兜底
                        onChange={(values) => setRejectCodes(values as string[])}
                        options={rejectDict.map((item) => ({value: item.code, label: item.name}))}
                      />
                    </div>
                  )}
                  {/* showCount 的计数渲染在输入框外右下角，留出下边距避免被按钮行遮住 */}
                  <div className="pb-4">
                    <Input.TextArea
                      rows={4}
                      maxLength={1000}
                      showCount
                      value={opinion}
                      onChange={(e) => setOpinion(e.target.value)}
                      placeholder={
                        t(
                          isCentral
                            ? 'podExempt.review.opinionPlaceholderFinal'
                            : 'podExempt.review.opinionPlaceholderFirst',
                        ) ?? ''
                      }
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button
                      type="primary"
                      block
                      disabled={!ready}
                      loading={submitting}
                      onClick={handleSubmit}
                    >
                      {t(isCentral ? 'podExempt.review.submitFinal' : 'podExempt.review.submitFirst')}
                    </Button>
                    <Button onClick={() => navigate(listUrl)}>{t('podExempt.review.later')}</Button>
                  </div>
                  <Typography.Text type="secondary" style={{fontSize: 12}}>
                    {t(isCentral ? 'podExempt.review.hintFinal' : 'podExempt.review.hintFirst')}
                  </Typography.Text>
                </div>
              )}
            </Section>
            <Section title={t('podExempt.detail.timeline')}>
              <PodExemptFlowTimeline flows={detail.flows} dictNameOf={dictNameOf} pendingNode={pendingNode} />
            </Section>
          </div>
        </div>
      )}
    </div>
  );
};

export default PodExemptReviewWorkbench;
